import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useStores } from '~store';
import { clampSpeed } from '~utils/speed';

import { MAX_VALID_SPEED_MS, MIN_VALID_SPEED_MS } from './constants';
import { useGetCycleSegments } from './hooks/useGetCycleSegments';
import { useIndicatorValues } from './hooks/useIndicatorValues';
import { useNoSignalBlocks } from './hooks/useNoSignalBlocks';
import { PauseInterval } from './hooks/usePauseSegments';
import { useTimeNormalization } from './hooks/useTimeNormalization';
import { useWholeHours } from './hooks/useWholeHours';
import { HourMarker } from './HourMarker';
import { IdleThreshold } from './IdleThreshold';
import NoSignal from './NoSegment';
import { PauseSegment } from './PauseSegment';
import SpeedSegment from './SpeedSegment';
import TimeIndicator from './TimeIndicator';

const Y_AXIS_SEGMENTS_COUNT = 4;
const SPEED_LEGEND_HEIGHT_IN_PX = 17;
const SPEED_LEGENDS_GAP_IN_PX = 4;

interface SpeedTimelineProps {
  pings: LocationHistoryMinimal[];
  pauseSegments: PauseInterval[];
}

export const SpeedTimeline = ({ pings, pauseSegments }: SpeedTimelineProps) => {
  const minTime = pings[0].occurredAt.toISOString();
  const maxTime = pings[pings.length - 1].occurredAt.toISOString();

  const { wholeHours, determineInterval } = useWholeHours(minTime, maxTime);
  const normalize = useTimeNormalization(minTime, maxTime);

  const maxSpeed = useMemo(
    () =>
      Math.max(
        ...pings
          .map((p) => Number(p.speed))
          .filter((s) => s > MIN_VALID_SPEED_MS && s <= MAX_VALID_SPEED_MS),
        0,
      ),
    [pings],
  );

  const lowerMiddleSpeed = 0.33 * maxSpeed;
  const upperMiddleSpeed = 0.66 * maxSpeed;

  const segments = useGetCycleSegments(pings);
  const noSignalBlocks = useNoSignalBlocks(segments);

  const { graphRef, indicatorValues } = useIndicatorValues(
    minTime,
    maxTime,
    noSignalBlocks,
  );

  const interval = determineInterval();

  const filteredHours = wholeHours.filter((hour, index) => index % interval === 0);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: 'auto 1fr',
          gridTemplateRows: `${SPEED_LEGEND_HEIGHT_IN_PX * Y_AXIS_SEGMENTS_COUNT + SPEED_LEGENDS_GAP_IN_PX * (Y_AXIS_SEGMENTS_COUNT - 1)}px`,
        }}
      >
        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            transform: `translateY(${SPEED_LEGEND_HEIGHT_IN_PX / 2 - SPEED_LEGENDS_GAP_IN_PX / 2}px)`,
          }}
        >
          <SpeedLegendLabel value={maxSpeed} />
          <SpeedLegendLabel value={upperMiddleSpeed} />
          <SpeedLegendLabel value={lowerMiddleSpeed} />
          <SpeedLegendLabel value={0} />
        </Box>

        {/* Graph */}
        <Box
          ref={graphRef}
          sx={{
            alignSelf: 'flex-end',
            bgcolor: 'background.paper',
            height: `calc(100% - ${SPEED_LEGEND_HEIGHT_IN_PX / 2}px)`,
            position: 'relative',
          }}
        >
          {[0, 33, 66, 100].map((percent) => (
            <Box
              key={percent}
              sx={{
                position: 'absolute',
                height: 2,
                width: '100%',
                top: `${percent}%`,
              }}
            />
          ))}

          <IdleThreshold maxSpeed={maxSpeed} />

          <TimeIndicator
            visible={indicatorValues.indicatorVisible}
            position={indicatorValues.indicatorPosition}
            time={indicatorValues.indicatorTime}
          />

          {filteredHours.map((hour) => (
            <HourMarker key={hour} position={normalize(hour)} time={hour} />
          ))}

          {/* No-signal gaps are a backdrop, so render them before the per-ping
              speed bars. A sparse ping is a 1px bar sitting at the edge of the
              following gap; drawing the gap on top would hide it (TRE-15314). */}
          {noSignalBlocks.map(([start, end]) => (
            <NoSignal
              key={`nosignal_${start}`}
              startPosition={normalize(start.toISOString())}
              endPosition={normalize(end.toISOString(), true)}
            />
          ))}

          {pings
            .filter(
              (p) =>
                p.speed !== null &&
                Number(p.speed) >= MIN_VALID_SPEED_MS &&
                Number(p.speed) <= MAX_VALID_SPEED_MS,
            )
            .map((p, index) => (
              <SpeedSegment
                key={`speed_${index}`}
                position={normalize(p.occurredAt.toISOString())}
                speed={clampSpeed(Number(p.speed) || 0)}
                maxSpeed={maxSpeed}
              />
            ))}

          {pauseSegments.map((seg, index) => (
            <PauseSegment
              key={`pause_${index}`}
              startPosition={normalize(seg.start.toISOString())}
              endPosition={normalize(seg.end.toISOString(), true)}
              isOpen={seg.isOpen}
            />
          ))}
        </Box>
      </Box>
    </>
  );
};

interface SpeedLegendLabelProps {
  value: number; // The speed in m/s
}

const SpeedLegendLabel = ({ value }: SpeedLegendLabelProps) => {
  const { userStore } = useStores();
  const isMetric = userStore?.userCompany.isMeters; // Fixed: was using isFeet

  // Convert speed from m/s to km/h
  const convertMStoKMPH = (speed: number) => speed * 3.6;

  // Convert speed from m/s to mi/h
  const convertMStoMPH = (speed: number) => speed * 2.23694;

  const speed = isMetric ? convertMStoKMPH(value) : convertMStoMPH(value);

  const unit = isMetric
    ? t('live_map.timeline.kilometer_per_hour')
    : t('live_map.timeline.mile_per_hour');

  return (
    <Box sx={{ height: `${SPEED_LEGEND_HEIGHT_IN_PX}px`, textAlign: 'right' }}>
      <Typography variant={'body2'}>{`${speed.toFixed(1)}\u00A0${unit}`}</Typography>
    </Box>
  );
};
